/**
 * PDP color variant hover preview.
 * Swaps only the first gallery image while hovering a non-active color variant.
 */
class VariantHoverGallery {
	constructor() {
		this.mediaQuery = window.matchMedia('(min-width: 1024px) and (hover: hover)');
		this.galleryElement = document.querySelector('.js-pdp-gallery');
		this.galleryWrapper = this.galleryElement ? this.galleryElement.querySelector('.swiper-wrapper') : null;
		this.firstSlide = this.galleryWrapper ? this.galleryWrapper.querySelector('.swiper-slide') : null;
		this.firstSlideImage = this.firstSlide ? this.firstSlide.querySelector('img') : null;
		this.variantItems = Array.from(document.querySelectorAll('.js-custom-product-variant[data-hover-media]'));

		if (!this.galleryElement || !this.firstSlide || !this.firstSlideImage || this.variantItems.length === 0) {
			return;
		}

		this.originalState = this.captureOriginalState();
		this.activePreviewKey = null;
		this.transitionTimer = null;

		this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);
		this.init();
	}

	init() {
		this.variantItems.forEach((item) => {
			item.addEventListener('mouseenter', (event) => this.handleVariantMouseEnter(event));
			item.addEventListener('mouseleave', (event) => this.handleVariantMouseLeave(event));
		});

		if (this.mediaQuery.addEventListener) {
			this.mediaQuery.addEventListener('change', this.handleMediaQueryChange);
		} else {
			this.mediaQuery.addListener(this.handleMediaQueryChange);
		}
	}

	isEnabled() {
		return this.mediaQuery.matches;
	}

	captureOriginalState() {
		return {
			slidePswpSrc: this.firstSlide.getAttribute('data-pswp-src'),
			slidePswpWidth: this.firstSlide.getAttribute('data-pswp-width'),
			slidePswpHeight: this.firstSlide.getAttribute('data-pswp-height'),
			imageSrc: this.firstSlideImage.getAttribute('src'),
			imageDataSrc: this.firstSlideImage.getAttribute('data-src'),
			imageAlt: this.firstSlideImage.getAttribute('alt'),
			imageWidth: this.firstSlideImage.getAttribute('width'),
			imageHeight: this.firstSlideImage.getAttribute('height'),
			imageClass: this.firstSlideImage.className,
			imageLoading: this.firstSlideImage.getAttribute('loading')
		};
	}

	handleMediaQueryChange(event) {
		if (!event.matches) {
			this.restoreOriginalPreview(true);
		}
	}

	handleVariantMouseEnter(event) {
		if (!this.isEnabled()) {
			return;
		}

		const variantItem = event.currentTarget;

		// Hovering the currently viewed variant should not trigger image reload.
		if (variantItem.classList.contains('is-active-element')) {
			this.restoreOriginalPreview();
			return;
		}

		const hoverMedia = this.parseHoverMedia(variantItem.dataset.hoverMedia);
		if (hoverMedia.length === 0) {
			this.restoreOriginalPreview();
			return;
		}

		const previewMedia = hoverMedia[0];
		const previewKey = previewMedia.src;

		if (this.activePreviewKey === previewKey) {
			return;
		}

		if (this.activePreviewKey === null && previewMedia.src === this.originalState.imageSrc) {
			return;
		}

		this.applyPreview(previewMedia, previewKey);
	}

	handleVariantMouseLeave(event) {
		if (!this.isEnabled()) {
			return;
		}

		const relatedTarget = event.relatedTarget;
		if (relatedTarget && relatedTarget.closest('.js-custom-product-variant')) {
			return;
		}

		this.restoreOriginalPreview();
	}

	parseHoverMedia(rawValue) {
		if (!rawValue) {
			return [];
		}

		try {
			const parsed = JSON.parse(rawValue);
			if (!Array.isArray(parsed)) {
				return [];
			}

			return parsed
				.filter((item) => item && typeof item.src === 'string' && item.src.trim() !== '')
				.map((item) => ({
					src: item.src,
					width: Number(item.width) > 0 ? Number(item.width) : 1200,
					height: Number(item.height) > 0 ? Number(item.height) : 1200,
					alt: typeof item.alt === 'string' ? item.alt : ''
				}));
		} catch (error) {
			return [];
		}
	}

	applyPreview(mediaItem, previewKey) {
		this.activePreviewKey = previewKey;

		this.runTransition(() => {
			this.firstSlide.setAttribute('data-pswp-src', mediaItem.src);
			this.firstSlide.setAttribute('data-pswp-width', String(mediaItem.width));
			this.firstSlide.setAttribute('data-pswp-height', String(mediaItem.height));

			this.firstSlideImage.removeAttribute('data-src');
			this.firstSlideImage.className = this.originalState.imageClass || 'image-item';
			this.firstSlideImage.setAttribute('src', mediaItem.src);
			this.firstSlideImage.setAttribute('alt', mediaItem.alt || '');
			this.firstSlideImage.setAttribute('width', String(mediaItem.width));
			this.firstSlideImage.setAttribute('height', String(mediaItem.height));
			this.firstSlideImage.setAttribute('loading', 'eager');

			this.galleryElement.classList.add('is-hover-preview');
		});
	}

	restoreOriginalPreview(force = false) {
		if (!force && this.activePreviewKey === null) {
			return;
		}

		this.runTransition(() => {
			this.applyOptionalAttribute(this.firstSlide, 'data-pswp-src', this.originalState.slidePswpSrc);
			this.applyOptionalAttribute(this.firstSlide, 'data-pswp-width', this.originalState.slidePswpWidth);
			this.applyOptionalAttribute(this.firstSlide, 'data-pswp-height', this.originalState.slidePswpHeight);

			this.applyOptionalAttribute(this.firstSlideImage, 'src', this.originalState.imageSrc);
			this.applyOptionalAttribute(this.firstSlideImage, 'data-src', this.originalState.imageDataSrc);
			this.applyOptionalAttribute(this.firstSlideImage, 'alt', this.originalState.imageAlt);
			this.applyOptionalAttribute(this.firstSlideImage, 'width', this.originalState.imageWidth);
			this.applyOptionalAttribute(this.firstSlideImage, 'height', this.originalState.imageHeight);
			this.applyOptionalAttribute(this.firstSlideImage, 'loading', this.originalState.imageLoading);
			this.firstSlideImage.className = this.originalState.imageClass || 'image-item';

			this.activePreviewKey = null;
			this.galleryElement.classList.remove('is-hover-preview');
		});
	}

	runTransition(callback) {
		if (this.transitionTimer) {
			window.clearTimeout(this.transitionTimer);
		}

		this.galleryElement.classList.add('is-fading-out');

		this.transitionTimer = window.setTimeout(() => {
			callback();
			this.galleryElement.classList.remove('is-fading-out');
		}, 90);
	}

	applyOptionalAttribute(element, attributeName, value) {
		if (value === null || value === undefined) {
			element.removeAttribute(attributeName);
			return;
		}

		element.setAttribute(attributeName, value);
	}
}

document.addEventListener('DOMContentLoaded', () => {
	new VariantHoverGallery();
});
